/**
 * @typedef {{
 * 	amountToLoan?: number | null,
 * 	amortizationYears?: number | null,
 * 	dueDate?: Date | string | null,
 * 	escrowInsuranceMonthly?: number | null,
 * 	escrowTaxMonthly?: number | null,
 * 	originalDateOfContract?: Date | string | null,
 * 	rate?: number | null,
 * }} MortgageDetails
 */

/**
 * @param {Date | string | null | undefined} value
 * @returns {string}
 */
export function formatFinanceWidgetDate(value) {
	if (!value) {
		return '';
	}

	const date = value instanceof Date ? value : new Date(value);

	return Number.isNaN(date.valueOf()) || date.valueOf() === 0
		? ''
		: date.toISOString();
}

/**
 * @param {number | null | undefined} value
 * @returns {string}
 */
export function formatFinanceWidgetNumber(value) {
	return value === null || value === undefined ? '' : String(value);
}

/**
 * @param {number | null | undefined} value
 * @returns {number | null}
 */
export function convertFinanceWidgetCentsToDollars(value) {
	return value === null || value === undefined ? null : value / 100;
}

/**
 * Required by the current widget embed contract for payment-based widgets.
 * @param {number | null | undefined} loanAmount
 * @param {number | null | undefined} annualRate
 * @param {number} amortizationYears
 * @returns {number | null}
 */
export function calculateFinanceWidgetMonthlyPayment(
	loanAmount,
	annualRate,
	amortizationYears
) {
	if (
		loanAmount === null ||
		loanAmount === undefined ||
		annualRate === null ||
		annualRate === undefined ||
		!amortizationYears
	) {
		return null;
	}

	const paymentCount = amortizationYears * 12;
	const monthlyRate = annualRate / 100 / 12;

	if (monthlyRate === 0) {
		return loanAmount / paymentCount;
	}

	const compoundRate = (1 + monthlyRate) ** paymentCount;

	return (loanAmount * monthlyRate * compoundRate) / (compoundRate - 1);
}

/**
 * @param {Date | string | null | undefined} originationDate
 * @param {Date} [currentDate]
 * @returns {number | null}
 */
export function calculateMonthsSinceOrigination(
	originationDate,
	currentDate = new Date()
) {
	if (!originationDate) {
		return null;
	}

	const startDate =
		originationDate instanceof Date
			? originationDate
			: new Date(originationDate);

	if (Number.isNaN(startDate.valueOf()) || startDate > currentDate) {
		return null;
	}

	const yearDifference = currentDate.getFullYear() - startDate.getFullYear();
	const monthDifference = currentDate.getMonth() - startDate.getMonth();
	const monthsFromYears = yearDifference * 12;
	let monthCount = monthsFromYears + monthDifference;

	if (currentDate.getDate() < startDate.getDate()) {
		monthCount -= 1;
	}

	return Math.max(monthCount, 0);
}

/**
 * @param {MortgageDetails} details
 * @param {Date} [currentDate]
 * @returns {number | null}
 */
export function calculateEstimatedCurrentBalance(
	details,
	currentDate = new Date()
) {
	const amortizationYears = details.amortizationYears ?? 30;
	const payment = calculateFinanceWidgetMonthlyPayment(
		details.amountToLoan,
		details.rate,
		amortizationYears
	);
	const monthsSinceOrigination = calculateMonthsSinceOrigination(
		details.originalDateOfContract,
		currentDate
	);

	if (
		details.amountToLoan === null ||
		details.amountToLoan === undefined ||
		details.rate === null ||
		details.rate === undefined ||
		payment === null ||
		monthsSinceOrigination === null
	) {
		return null;
	}

	const monthlyRate = details.rate / 100 / 12;

	if (monthlyRate === 0) {
		const paidPrincipal = payment * monthsSinceOrigination;

		return Math.max(details.amountToLoan - paidPrincipal, 0);
	}

	const compoundRate = (1 + monthlyRate) ** monthsSinceOrigination;
	const compoundedOriginalBalance = details.amountToLoan * compoundRate;
	const compoundedPaidBalance = (payment / monthlyRate) * (compoundRate - 1);
	const balance = compoundedOriginalBalance - compoundedPaidBalance;

	return Math.max(balance, 0);
}

/**
 * @param {Map<string, {valueMean?: number | null, date?: Date | null}[]> | null | undefined} valuations
 * @returns {number | null}
 */
export function getLatestQuantariumValueInDollars(valuations) {
	const quantariumValuations = valuations?.get('quantarium') ?? [];
	const validValuations = quantariumValuations.filter(
		(valuation) => valuation?.valueMean
	);

	if (!validValuations.length) {
		return null;
	}

	const latestValuation =
		validValuations
			.filter((valuation) => valuation.date !== null)
			.sort(
				(a, b) => (b.date?.valueOf() ?? 0) - (a.date?.valueOf() ?? 0)
			)[0] ?? validValuations[0];

	return convertFinanceWidgetCentsToDollars(latestValuation.valueMean);
}

/**
 * @param {number | null | undefined} quantariumValue
 * @param {number | null | undefined} currentBalance
 * @returns {number | null}
 */
export function calculateEstimatedCurrentEquity(
	quantariumValue,
	currentBalance
) {
	if (
		quantariumValue === null ||
		quantariumValue === undefined ||
		currentBalance === null ||
		currentBalance === undefined
	) {
		return null;
	}

	return quantariumValue - currentBalance;
}

/**
 * @param {{
 * 	accessToken?: string | null,
 * 	agentId?: string | null,
 * 	details: MortgageDetails,
 * 	estimatedHomeValue?: number | null,
 * 	userId?: string | null,
 * 	userType: 'agent' | 'homeowner',
 * }} data
 * @returns {{attributes: {[key: string]: string}, reloadKey: string}}
 */
export function buildFinanceWidgetConfiguration(data) {
	const { details } = data;
	const amortizationYears = details.amortizationYears ?? 30;
	const estimatedHomeValue = data.estimatedHomeValue ?? null;
	const monthlyPayment = calculateFinanceWidgetMonthlyPayment(
		details.amountToLoan,
		details.rate,
		amortizationYears
	);
	const estimatedCurrentBalance = calculateEstimatedCurrentBalance(details);
	const mortgageBalance =
		estimatedCurrentBalance ?? details.amountToLoan ?? null;
	const estimatedEquity =
		estimatedHomeValue === null ||
		mortgageBalance === null ||
		mortgageBalance === undefined
			? null
			: Math.max(estimatedHomeValue - mortgageBalance, 0);
	const formatNumber = formatFinanceWidgetNumber;
	const formatDate = formatFinanceWidgetDate;

	return {
		attributes: {
			'data-access-token': data.accessToken ?? '',
			'data-agent': data.agentId ?? '',
			'data-amortization-months': String(amortizationYears * 12),
			'data-api-base-url': `${window.location.origin}/api/v1`,
			'data-average-avm-value': formatNumber(estimatedHomeValue),
			'data-avm-value': formatNumber(estimatedHomeValue),
			'data-current-avm-value': formatNumber(estimatedHomeValue),
			'data-current-balance': formatNumber(mortgageBalance),
			'data-current-interest-rate': formatNumber(details.rate),
			'data-default-extra-payment': '100',
			'data-default-lump-sum-amount': '1000',
			'data-estimated-current-balance': formatNumber(
				estimatedCurrentBalance
			),
			'data-estimated-equity': formatNumber(estimatedEquity),
			'data-estimated-home-value': formatNumber(estimatedHomeValue),
			'data-estimated-principal-and-interest':
				formatNumber(monthlyPayment),
			'data-interest-rate': formatNumber(details.rate),
			'data-loan-amount': formatNumber(details.amountToLoan),
			'data-monthly-payment': formatNumber(monthlyPayment),
			'data-mortgage-balance': formatNumber(mortgageBalance),
			'data-mortgage-due-date': formatDate(details.dueDate),
			'data-mortgage-original-date': formatDate(
				details.originalDateOfContract
			),
			'data-original-date': formatDate(details.originalDateOfContract),
			'data-original-loan-amount': formatNumber(details.amountToLoan),
			'data-purchase-price': formatNumber(details.amountToLoan),
			'data-tenant': window.location.host,
			'data-tenant-id': window.location.host,
			'data-user-id': data.userId ?? '',
			'data-user-type': data.userType,
		},
		reloadKey: [
			details.amountToLoan,
			details.rate,
			amortizationYears,
			details.escrowTaxMonthly,
			details.escrowInsuranceMonthly,
			formatDate(details.originalDateOfContract),
			estimatedCurrentBalance,
			estimatedHomeValue,
		].join(':'),
	};
}
